/**
 * Normalization of custom enum options across JSON and legacy comma/newline storage formats.
 *
 * @module apps/web/src/lib/attributeOptions
 */

import type { AttributeDef } from '@fluxability/types';

/**
 * Read enum choices from an array, JSON array, or legacy comma/newline list; missing choices yield
 * an empty array.
 */
export function parseAttributeOptions(attr: Pick<AttributeDef, 'options'>): string[] {
  if (!attr.options) return [];
  if (Array.isArray(attr.options)) return attr.options as unknown as string[];
  try {
    const parsed = JSON.parse(attr.options);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {
    // Fall through to comma/newline separated legacy values.
  }
  return String(attr.options)
    .split(/[\n,]/)
    .map(option => option.trim())
    .filter(Boolean);
}

/**
 * Return a definition with normalized, case-insensitively deduplicated, locale-sorted enum choices
 * while preserving its other metadata. Blank additions return the original definition.
 */
export function withAddedAttributeOption(attr: AttributeDef, option: string): AttributeDef {
  const clean = normalizeAttributeOption(option);
  if (!clean) return attr;
  const values = new Map<string, string>();
  for (const existing of parseAttributeOptions(attr)) {
    const normalized = normalizeAttributeOption(existing);
    if (normalized) values.set(normalized.toLowerCase(), normalized);
  }
  values.set(clean.toLowerCase(), clean);
  return {
    ...attr,
    data_type: 'enum',
    options: JSON.stringify([...values.values()].sort((a, b) => a.localeCompare(b))),
  };
}

/**
 * Trim an option and collapse consecutive whitespace to a single space.
 */
export function normalizeAttributeOption(option: unknown): string {
  return String(option ?? '').trim().replace(/\s+/g, ' ');
}
